import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { ShareListUser } from '../users/types';
import { toShareListUsers } from '../users/utils';
import { attachId, attachIds } from '../utils';

import {
  Category, Video,
} from './types';
import {
  toVideo, toVideos,
} from './utils';

export const videosApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getVideos: build.query<CognitoAPIResponse<Video[]>,
    {title?: string, subject?: string, topic?: string}>({
      query(arg) {
        const { title, subject, topic } = arg;
        return {
          url: 'videos/query',
          method: 'GET',
          params: {
            title, subject, topic,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Videos' as const, id })), 'Videos']
        : ['Videos']),
      transformResponse(response: any) {
        return toVideos(response);
      },
    }),
    getVideo: build.query<CognitoAPIResponse<Video>,
    {subject: string, videoId: string}>({
      query(arg) {
        const { subject, videoId } = arg;
        return {
          url: `videos/${subject}/${videoId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{ type: 'Videos', id: arg?.videoId }],
      transformResponse(response: any) {
        return toVideo(response);
      },
    }),
    getVideoCategories: build.query<CognitoAPIResponse<Category[]>, { subject: string }>({
      query(arg) {
        const { subject } = arg;
        return {
          url: `videos/category/${subject}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg?.subject,
      }],
      transformResponse(response: any) {
        return attachIds(response, (entry) => `${entry.subject}#${entry.category}`);
      },
    }),
    getVideoCategory: build.query<CognitoAPIResponse<Category>,
    { subject: string, category: string }>({
      query(arg) {
        const { subject, category } = arg;
        return {
          url: `videos/category/${subject}/${category}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry) => `${entry.subject}#${entry.category}`);
      },
    }),
    getVideoUsers: build.query<CognitoAPIResponse<ShareListUser[]>, {
      subject: string,
      category?: string,
      topic?: string
    }>({
      query(arg) {
        const { subject, category, topic } = arg;

        return {
          url: 'videos/users',
          method: 'GET',
          params: {
            subject, category, topic,
          },
        };
      },
      providesTags: (result, error, arg) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'VideoPermissions' as const, id })), {
          type: 'VideoPermissions', id: `${arg.subject}#${arg.category}#${arg.topic}`,
        }]
        : [{
          type: 'VideoPermissions', id: `${arg.subject}#${arg.category}#${arg.topic}`,
        }]),
      transformResponse(response: any) {
        return toShareListUsers(response);
      },
    }),
    shareVideo: build.mutation<any, {
      userId: string,
      subject: string,
      category?: string,
      topic?: string
    } >({
      query(arg) {
        const { userId, subject, category, topic } = arg;

        return {
          url: 'videos/share',
          body: {
            userId, subject, category, topic,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoPermissions',
        id: arg.userId,
      }, {
        type: 'VideoPermissions',
        id: `${arg.subject}#${arg.category}#${arg.topic}`,
      }],
    }),
    unshareVideo: build.mutation<any, {
      userId: string,
      subject: string,
      category?: string,
      topic?: string
    } >({
      query(arg) {
        const { userId, subject, category, topic } = arg;

        return {
          url: 'videos/unshare',
          body: {
            userId, subject, category, topic,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoPermissions',
        id: arg.userId,
      }, {
        type: 'VideoPermissions',
        id: `${arg.subject}#${arg.category}#${arg.topic}`,
      }],
    }),
    newVideoCategory: build.mutation<any, { subject: string, attributes: any }>({
      query(arg) {
        const { subject, attributes } = arg;

        return {
          url: 'videos/category/new',
          body: { ...attributes, subject },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    newVideoTopic: build.mutation<any, { subject: string, category: string, attributes: any }>({
      query(arg) {
        const { subject, category, attributes } = arg;

        return {
          url: `videos/category/${subject}/${category}/topic/new`,
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    newVideo: build.mutation<any, { subject: string, category: string, attributes: any }>({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'videos/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }, 'Videos'],
    }),
    updateVideoCategory:
    build.mutation<any, { subject: string, category: string, attributes: any }>({
      query(arg) {
        const { subject, category, attributes } = arg;

        return {
          url: `videos/category/${subject}/${category}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    updateVideoTopic:
    build.mutation<any, { subject: string, category: string, topic: string, attributes: any }>({
      query(arg) {
        const { subject, category, topic, attributes } = arg;

        return {
          url: `videos/category/${subject}/${category}/${topic}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    updateVideo: build.mutation<any, { subject: string, videoId: string, attributes: any }>({
      query(arg) {
        const { subject, videoId, attributes } = arg;

        return {
          url: `videos/${subject}/${videoId}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'Videos',
        id: arg.videoId,
      }, {
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    deleteVideoCategory: build.mutation<any, { subject: string, category: string }>({
      query(arg) {
        const { subject, category } = arg;

        return {
          url: `videos/category/${subject}/${category}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }],
    }),
    deleteVideoTopic: build.mutation<any, { subject: string, category: string, topic: string }>({
      query(arg) {
        const { subject, category, topic } = arg;

        return {
          url: `videos/category/${subject}/${category}/${topic}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['VideoCategories'],
    }),
    deleteVideo: build.mutation<any, {
      subject: string,
      videoId: string,
      category: string
    }>({
      query(arg) {
        const { subject, videoId } = arg;

        return {
          url: `videos/${subject}/${videoId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'VideoCategories',
        id: arg.subject,
      }, {
        type: 'Videos',
        id: arg.videoId,
      }],
    }),
  }),
});

export const { useGetVideosQuery,
  useGetVideoQuery,
  useGetVideoCategoriesQuery,
  useNewVideoCategoryMutation,
  useGetVideoCategoryQuery,
  useGetVideoUsersQuery,
  useShareVideoMutation,
  useUnshareVideoMutation,
  useNewVideoTopicMutation,
  useUpdateVideoCategoryMutation,
  useUpdateVideoTopicMutation,
  useNewVideoMutation,
  useDeleteVideoCategoryMutation,
  useDeleteVideoMutation,
  useDeleteVideoTopicMutation,
  useUpdateVideoMutation } = videosApi;
